import { makeAutoObservable, action } from 'mobx'
import { storeMobx, cartMobx, discountMobx, restaurantCenter, RESTAURANT_EVENT } from 'restaurant'
import { themeMobx, queueOrderingMobx, eatTypeStatusMobx } from '@mobxStore'
import { isArray, keyBy, isObject, isEmpty } from '@dt/fe-utils/dist/lodash'
import { getColorIsDark, getHistoryNew, spliceZhLength } from '@utils'
import { TEEatType } from 'restaurant/mobx/store.mobx'

const setTabAndColor = (themeColor) => {
  if (themeColor) {
    const colorPropertyMap: string[] = [
      'ion-color-primary', // 基础主题色
      'ion-color-primary-shade', // shadow
      'ion-color-primary-tint',
    ]
    colorPropertyMap.forEach((colorProperty) => {
      document.body.style.setProperty(`--${colorProperty}`, themeColor)
    })
    const isDark = getColorIsDark(themeColor)
    document.body.style.setProperty('--ion-color-inverse', isDark ? '#fff' : '#161616')
  }
}

const setProductSizeColor = (themeColor) => {
  if (themeColor) {
    const colorPropertyMap: string[] = [
      'ion-color-product', // 基础主题色
    ]
    colorPropertyMap.forEach((colorProperty) => {
      document.body.style.setProperty(`--${colorProperty}`, themeColor)
    })
  }
}

const defaultGlobalDecorates: any = {
  themeColor: '#d00218'
}

const defaultMallGlobalDecorates: any = {
  collect: {
    home: '',
    img: {
      key: 'shopcart',
      type: 'icon',
      value: 'mall-shopcart2',
      icons: ['mall-shopcart2', 'mall-shopcart3', 'mall-shopcart1']
    },
  },
  // 主题颜色
  themeColor: '#161616',
  groupBuyShare: {
    title: '',
    image: ''
  },
  cartAndBuy: {
    cart: '',
    buy: ''
  }
}

const defaultHomeDecorates: any = {
}

const defaultMealDecorates: any = {
}

const defaultDetailDecorates: IDetailDecorates = {
  openFullNFree: 0,
  openFoldSoldoutScale: 1,
}

const defaultMktDecorates = {
  member: {
    bgType: 'color',
    bgValue: '#f4f4f4',
    fontColor: '#161616'
  },
  entry: [
    // 申领会员卡
    {
      key: 'applyMemberCard',
      type: 'icon',
      value: 'zuanshi',
      color: '#161616',
      icons: ['zuanshi']
    },
    // 我的优惠券
    {
      key: 'myCoupon',
      type: 'icon',
      color: '#161616',
      value: 'youhuiquan',
      icons: ['youhuiquan']
    },
    // 积分兑换
    {
      key: 'redeem',
      type: 'icon',
      color: '#161616',
      value: 'jifen1',
      icons: ['jifen1']
    },
    // 赠券领取
    // {
    //   key: 'receiveCoupon',
    //   type: 'icon',
    //   color: '#161616',
    //   value: 'gift',
    //   icons: ['gift']
    // },
    // 团购商城
    {
      key: 'groupCoupon',
      type: 'icon',
      color: '#161616',
      value: 'a-32',
      icons: ['a-32']
    },
    // 我的礼品卡
    {
      key: 'myGiftCard',
      type: 'icon',
      color: '#161616',
      value: 'giftcard',
      icons: ['giftcard']
    },
    // 信用卡管理
    {
      key: 'myGreditCard',
      type: 'icon',
      color: '#161616',
      value: 'yinhangka',
      icons: ['yinhangka']
    },
    // 我的发票
    {
      key: 'myInvoice',
      type: 'icon',
      color: '#161616',
      value: 'invoice',
      icons: ['invoice']
    },
    // 订单
    {
      key: 'order',
      type: 'icon',
      color: '#161616',
      value: 'orders2',
      icons: ['orders2']
    },
    // 设置
    {
      key: 'setting',
      type: 'icon',
      color: '#161616',
      value: 'setting',
      icons: ['setting']
    }
  ],
  market: [
    // 集点活动
    {
      key: 'setPoint',
      type: 'fixed',
    },
    // 领取优惠券
    {
      key: 'receiveCoupon',
      type: 'fixed',
    },
    // 礼品卡
    {
      key: 'giftCard',
      type: 'fixed',
      showCnt: 'all'
    },
    // 抽奖活动
    {
      key: 'luckyDraw',
      type: 'fixed',
    },
  ],
  notMarket: []
}

/** 手机装修mobx */
class DecorationStore {
  constructor () {
    makeAutoObservable(
      this,
      {
        setTabBarVisible: action,
      },
      { autoBind: true }
    )
  }

  inited: boolean = false

  decoratesStoreId: number = 0

  diyMemberCardInfo: IMemberCardInfo = null

  // 全局装修数据
  globalDecorates = defaultGlobalDecorates as IGlobalDecorates

  /** 首页装修数据 */
  homeDecorates = defaultHomeDecorates as IHomeDecorates

  /** 点餐页装修数据 */
  mealDecorates = defaultMealDecorates as TMealCommonDecorates

  /** 点餐页装修数据 */
  detailDecorates = defaultMealDecorates as IDetailDecorates

  /** 会员首页装修数据 */
  mktDecorates = defaultMktDecorates as any

  /** 商城全局装修数据 */
  mallGlobalDecorates = defaultMallGlobalDecorates as IMallGlobalDecorates

  /** 装修模板数据接口调用成功 */
  getDecorationTemplateSuccess: boolean = false

  /** tabbar是否可见 */
  tabBarVisible: boolean = true

  /** searchbar是否可见 */
  searchBarVisible: boolean = false

  /** list的头部分类bar 是否可见 */
  headerCategoryBarVisible: boolean = false

  /** 点餐页滚动轴Y的高度 */
  menuScrollTop: number = 0

  /** 当前pathName */
  pathName: string = ''

  /** 点餐页顶部高度 */
  get menuHeaderHeight () {
    const { noticeConfig } = storeMobx.info
    const showNotice = noticeConfig?.showCustom?.content || (noticeConfig?.showMktInfo && discountMobx.activities?.length) || noticeConfig?.showStoreInfo
    const showQueue = queueOrderingMobx.inPreQueueOrdering
    return (showNotice ? 22 : 0) + (showQueue ? 40 : 0) + 56
  }

  /** 点餐页切换入口 */
  get eatTypeEntries () {
    const isOld = isArray(this.mealDecorates?.entries)
    let entries = (isOld ? this.mealDecorates?.entries : []) as IMealEntriesItem[]
    if (!isOld && this.mealDecorates?.entries) {
      if (Config.codeType === 'brand') {
        entries = (this.mealDecorates?.entries['brandCode'] as IMealEntriesItem[]).map((item) => {
          return {
            ...item,
            name: !!item.name && spliceZhLength(item.name, 8)
          }
        })
      } else {
        entries = this.mealDecorates?.entries['storeCode'] as IMealEntriesItem[]
      }
    } else {
      if (!entries?.length) {
        if (Config.codeType === 'brand') {
          entries = [
            { name: '', value: 'pickup', checked: 1 },
            { name: '', value: 'delivery', checked: 1 }
          ]
        } else {
          entries = [
            { name: '', value: 'dinein', checked: 1 },
            { name: '', value: 'togo', checked: 1 }
          ]
        }
      }
    }
    return entries.filter(item => {
      let flag: boolean = false
      if (Config.codeType === 'brand') {
        if (![TEEatType.delivery, TEEatType.dinein, TEEatType.pickup].includes(item.value as TEEatType)) {
          flag = false
        } else {
          if (item.value === TEEatType.delivery && !storeMobx.info.openSelfDelivery) {
            flag = false
          } else if (item.value === TEEatType.dinein && !storeMobx.info.openDinein) {
            flag = false
          } else if (item.value === TEEatType.pickup && !storeMobx.info.openPickup) {
            flag = false
          } else {
            flag = true
          }
        }
      } else {
        if (![TEEatType.dinein, TEEatType.togo].includes(item.value as TEEatType)) {
          flag = false
        } else {
          flag = true
        }
      }
      return flag && !!item.checked
    }) as IMealEntriesItem[]
  }

  /** 点餐页切换入口 */
  get eatTypeEntriesMap () {
    return keyBy(this.eatTypeEntries, 'value')
  }

  /** 获取默认点餐方式 */
  get defaultEatType () {
    let entries = this.eatTypeEntries
    let eatType: any = entries.length ? entries[0].value : (Config.codeType === 'brand' ? TEEatType.pickup : storeMobx.info.eatType[0])
    if (!isEmpty(this.eatTypeEntriesMap) && !this.eatTypeEntriesMap[eatType]) {
      eatType = this.eatTypeEntries[0]?.value
    }
    if (cartMobx.info.eatType === TEEatType.delivery || eatType === TEEatType.delivery) {
      if (storeMobx.info.openSelfDelivery) {
        eatType = TEEatType.delivery
      } else {
        eatType = storeMobx.info.eatType[0]
      }
    }
    const { enableEatTypeList } = eatTypeStatusMobx
    console.log(entries, this.eatTypeEntriesMap, eatType)
    console.log(enableEatTypeList)
    if (enableEatTypeList.length && !enableEatTypeList.includes(eatType)) {
      eatType = enableEatTypeList[0]
    }
    if (Config.codeType === 'brand' && !storeMobx.info.storeId) {
      eatType = ''
    }
    return eatType
  }

  get fullOfBarAndTabBar () {
    const inPath = ['/menu'].includes(this.pathName)
    const isFloatTarBar = themeMobx?.data?.tabBar?.enablePosition !== 1
    const showTabMap = keyBy(themeMobx.showTabs, 'href')
    const hasTabMap = keyBy(themeMobx.showTabsPages, 'href')
    const isFullOfBar = !!discountMobx.discountResult.designatedBuyAgain
    return inPath && isFloatTarBar && showTabMap[this.pathName] && hasTabMap[this.pathName] && isFullOfBar
  }

  /** 设置装修模版数据 */
  setDecorateInfos (decoratesDetails: IDecorateDetails, manualSwitch: boolean, type: 'store' | 'brand') {
    const { globalDecorates, homeDecorates, mealDecorates, mktDecorates, mallGlobalDecorates } = decoratesDetails
    this.globalDecorates = globalDecorates || defaultGlobalDecorates
    this.homeDecorates = homeDecorates || defaultHomeDecorates
    this.mktDecorates = mktDecorates || defaultMktDecorates
    this.mallGlobalDecorates = mallGlobalDecorates || defaultMallGlobalDecorates
    if (mealDecorates) {
      this.mealDecorates = mealDecorates[mealDecorates.displayMode] || mealDecorates.classic
      this.mealDecorates.showHot = 0
      this.detailDecorates = mealDecorates.detailDecorates || defaultDetailDecorates
    }
    // 数据兼容处理
    if (homeDecorates) {
      if (homeDecorates.entrySetting && !homeDecorates.entrySetting.brandCode) {
        this.homeDecorates.entrySetting.brandCode = homeDecorates.entrySetting as any
        this.homeDecorates.entrySetting.brandCode?.entries?.forEach(item => {
          item.mode = item.mode === undefined ? this.homeDecorates.entrySetting.brandCode.mode : item.mode
        })
      }
      if (homeDecorates.entrySetting && !homeDecorates.entrySetting.storeCode) {
        this.homeDecorates.entrySetting.storeCode = homeDecorates.entrySetting as any
        this.homeDecorates.entrySetting.storeCode?.entries?.forEach(item => {
          item.mode = item.mode === undefined ? this.homeDecorates.entrySetting.storeCode.mode : item.mode
        })
      }
      if (!isObject(homeDecorates.backgroundImage)) {
        this.homeDecorates.backgroundImage = {
          url: homeDecorates.backgroundImage
        } as any
      }
    }
    if (mealDecorates && !globalDecorates) {
      this.globalDecorates.themeColor = this.mealDecorates?.themeColor || defaultGlobalDecorates.themeColor
    }
    this.homeDecorates.memberEntries = this.homeDecorates.memberEntries?.filter((m: any) => m?.value ? m?.isShow : true).map((m: any) => m?.value ? m.value : m)
    this.homeDecorates.sortContent = this.homeDecorates.sortContent || ['memberEntries', 'follow']
    if (this.globalDecorates.isBtnAndCartUseColor !== 0) {
      setTabAndColor(this.globalDecorates.themeColor)
    }
    if (this.globalDecorates.isProductSizeUseColor !== 0 && this.globalDecorates.themeColor) {
      document.body.style.setProperty('--ion-color-product', this.globalDecorates.themeColor)
    }
    if (!manualSwitch) {
      const _eatType = Config.eatType && eatTypeStatusMobx.isEatTypeEnable(Config.eatType) ? Config.eatType : this.defaultEatType
      if (_eatType && !Config.buyTogetherId) {
        const history = getHistoryNew()
        const isSync = history?.getPrevState() === '/store'
        cartMobx.setEatType(_eatType, { isSync })
      }
    }
    this.setInited(type)
  }

  /** 设置隐藏tabBar */
  setTabBarVisible (visible: boolean) {
    if (this.tabBarVisible !== visible) {
      this.tabBarVisible = visible
    }
  }

  /** 设置隐藏searchBar */
  setSearchBarVisible (visible: boolean) {
    if (this.searchBarVisible !== visible) {
      this.searchBarVisible = visible
    }
  }

  /** 设置隐藏headerCategorytabbar */
  setHeaderCategoryBarVisible (visible: boolean) {
    if (this.headerCategoryBarVisible !== visible) {
      this.headerCategoryBarVisible = visible
    }
  }

  /** 设置点餐页滚动距离 */
  setMenuScrollTop (hst: number) {
    if (this.menuScrollTop !== hst) {
      this.menuScrollTop = hst
    }
  }

  /** 设置初始化 */
  setInited (type: 'store' | 'brand') {
    this.inited = true
    restaurantCenter.emit(RESTAURANT_EVENT.MENU_OPEN_SOLDOUT, {
      type,
      openSoldoutHidden: this.mealDecorates?.openSoldoutHidden || 0,
      openFoldSoldout: this.mealDecorates?.openFoldSoldout || 0,
      openFoldSoldoutScale: this.detailDecorates?.openFoldSoldoutScale || 0,
      openSoldout: this.mealDecorates?.openSoldout || 0
    })
  }

  /** pathname */
  setPathName (pathName: string) {
    this.pathName = pathName
  }

  setDecoratesStoreId (storeId: number) {
    this.decoratesStoreId = storeId
  }

  setDiyMemberCardInfo (data: IMemberCardInfo) {
    this.diyMemberCardInfo = data
  }
}

export default new DecorationStore()
